import { DEFAULT_PAGE_INDEX_INPUT, DEFAULT_PAGE_SIZE } from "constants/index";

import { useRef, useState } from "react";

import { Grid, styled } from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import {
  GridColDef,
  GridFeatureMode,
  GridInputRowSelectionModel,
  GridRowModel,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-pro";
import noDataSrc from "assets/images/no-data.png";
import DataGrid from "components/DataGrid";
import CheckSquareIcon from "components/Icon/CheckSquare";
import Square from "components/Icon/Square";
import { useSearchParams } from "hooks/useSearchParams";
import IndexComponent from "pages/Components/FilterElements";
import { useLocation } from "react-router-dom";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";

function isEmpty(obj: object) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }
  return true;
}

const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const CustomNoRowsOverlay = () => {
  return (
    <StyledGridOverlay>
      <Grid textAlign="center">
        <Grid marginBottom={4}>
          <img src={noDataSrc} alt="no-data" />
        </Grid>
        <Grid fontSize="16px" fontWeight="600" marginBottom={1}>
          No data found
        </Grid>
        <Grid fontSize="14px">
          No data has been added yet. Please try adding a new one.
        </Grid>
      </Grid>
    </StyledGridOverlay>
  );
};

interface Props<RowData> {
  filterForm?: React.ReactNode;
  columns: GridColDef<any>[];
  rows: RowData[];

  paginationMode: GridFeatureMode | undefined;
  paginationModel: {
    page: number;
    pageSize: number;
  };
  rowCount: number;
  pageSizeOptions?:
    | (
        | number
        | {
            value: number;
            label: string;
          }
      )[]
    | undefined;
  isLoading: boolean;
  isNoFilter?: boolean;
  heightTable?: number;
  isPaginationModal: boolean;
  checkboxSelection?: boolean;
  isRowSelectable?:
    | ((params: GridRowParams<GridRowModel>) => boolean)
    | undefined;
  onRowSelectionModelChange?:
    | ((rowSelectionModel: GridRowSelectionModel, details: any) => void)
    | undefined;
  rowSelectionModel?: GridInputRowSelectionModel | undefined;
}
export const Table = (props: Props<any>) => {
  const myRef = useRef(null);
  const {
    filterForm,
    columns,
    rows,
    pageSizeOptions,
    paginationMode,
    paginationModel,
    rowCount,
    isLoading,
    isNoFilter,
    heightTable,
    isPaginationModal,
    checkboxSelection,
    isRowSelectable,
    onRowSelectionModelChange,
    rowSelectionModel,
  } = props;
  const { search } = useLocation();
  const searchParams = useSearchParams(search);
  const searchParamsOpen = searchParams;
  delete searchParamsOpen?.page;
  delete searchParamsOpen?.pageSize;
  delete searchParamsOpen?.pageModal;
  delete searchParamsOpen?.pageSizeModal;
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(
    !isEmpty(searchParamsOpen)
  );
  const [query, setQuery] = useQueryParams({
    pageIndex: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSize: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
    pageIndexModal: withDefault(NumberParam, DEFAULT_PAGE_INDEX_INPUT),
    pageSizeModal: withDefault(NumberParam, DEFAULT_PAGE_SIZE),
  });

  return (
    <Box>
      {isNoFilter ? null : <IndexComponent>{filterForm}</IndexComponent>}
      <Box
        sx={{
          marginTop: "14px",
          // height: heightTable ? `${heightTable}px` : "800px",
          height: heightTable ? `${heightTable}px` : "100%",
        }}
      >
        <DataGrid
          // ref={myRef}
          loading={isLoading}
          rows={rows}
          columns={columns}
          pagination
          paginationMode={paginationMode}
          paginationModel={paginationModel}
          rowCount={rowCount}
          pageSizeOptions={pageSizeOptions}
          onPaginationModelChange={(model, details) => {
            if (isPaginationModal) {
              setQuery({
                pageIndexModal: model.page + 1,
                pageSizeModal: model.pageSize,
              });
            } else {
              setQuery({
                pageIndex: model.page + 1,
                pageSize: model.pageSize,
              });
            }
          }}
          rowHeight={5}
          containerHeight={heightTable ?? 800}
          autoHeight={false}
          initialState={{ pinnedColumns: { right: ["id"] } }}
          checkboxSelection={checkboxSelection}
          isRowSelectable={isRowSelectable}
          onRowSelectionModelChange={onRowSelectionModelChange}
          rowSelectionModel={rowSelectionModel}
          rowSelection
          slots={{
            // eslint-disable-next-line
            baseCheckbox: (props) => {
              return (
                <Checkbox
                  {...props}
                  checkedIcon={<CheckSquareIcon />}
                  icon={<Square bgColor={props?.disabled} />}
                />
              );
            },
            // eslint-disable-next-line
            noResultsOverlay: CustomNoRowsOverlay,
            // eslint-disable-next-line
            noRowsOverlay: CustomNoRowsOverlay,
          }}
        />
      </Box>
    </Box>
  );
};
